/**
 * Queue Hooks (Application)
 *
 * React Query hooks for queue data fetching.
 * Uses the repository pattern from clean architecture.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { queueRepository } from '../../infrastructure/repositories';

import type { CallQueuePaginatedRequest, ReorderRequest, CallQueueDeleteRequest, SubmitCallDetailRequest, CaseAssignCallQueueRequest } from '../../domain/types';

// ============================================================================
// Query Keys Factory Pattern
// ============================================================================

export const queueKeys = {
  all: ['queue'] as const,
  lists: () => [...queueKeys.all, 'list'] as const,
  list: (request?: CallQueuePaginatedRequest) => [...queueKeys.lists(), request] as const,
  callDetails: () => [...queueKeys.all, 'call-detail'] as const,
  callDetail: (callId: number | null) => [...queueKeys.callDetails(), callId] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to fetch paginated queue list
 */
export function useQueueList(request: CallQueuePaginatedRequest) {
  return useQuery({
    queryKey: queueKeys.list(request),
    queryFn: () => queueRepository.getQueueList(request),
    staleTime: 0, // Always fetch fresh data for real-time queue
    refetchOnWindowFocus: true,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook to move/reorder queue items
 */
export function useMoveQueue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: ReorderRequest) => queueRepository.moveQueue(request),
    onSuccess: () => {
      // Invalidate all queue lists to refetch
      queryClient.invalidateQueries({ queryKey: queueKeys.lists() });
    },
  });
}

/**
 * Hook to delete queue items
 */
export function useDeleteQueues() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (requests: CallQueueDeleteRequest[]) => queueRepository.deleteQueues(requests),
    onSuccess: () => {
      // Invalidate all queue lists to refetch
      queryClient.invalidateQueries({ queryKey: queueKeys.lists() });
    },
  });
}

/**
 * Hook to bulk add cases to call queue
 */
export function useBulkAddCasesToQueue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CaseAssignCallQueueRequest) => queueRepository.bulkAddCasesToQueue(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queueKeys.lists() });
    },
  });
}

// ============================================================================
// Call Detail Hooks
// ============================================================================

/**
 * Hook to fetch call detail for a call
 * Only fetches when callId is provided (not null)
 */
export function useCallDetail(callId: number | null) {
  return useQuery({
    queryKey: queueKeys.callDetail(callId),
    queryFn: () => queueRepository.getCallDetail(callId!),
    enabled: callId !== null,
  });
}

/**
 * Hook to submit call detail
 */
export function useSubmitCallDetail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ callId, data }: { callId: number; data: SubmitCallDetailRequest }) => queueRepository.submitCallDetail(callId, data),
    onSuccess: () => {
      // Invalidate queue lists to refresh after submission
      queryClient.invalidateQueries({ queryKey: queueKeys.lists() });
    },
  });
}

// ============================================================================
// Contact / Case Search Hooks (for inbound call detail)
// ============================================================================

/**
 * Hook to search contacts for associating with an inbound call.
 * Only fetches when callId is provided and query is at least 2 characters.
 */
export function useContactSearch(callId: number | null, query: string) {
  return useQuery({
    queryKey: [...queueKeys.all, 'contact-search', callId, query] as const,
    queryFn: () => queueRepository.searchContacts(callId!, query),
    enabled: callId !== null && query.trim().length >= 2,
  });
}

/**
 * Hook to search cases linked to a contact for associating with an inbound call.
 * Only fetches when both callId and contactId are provided.
 */
export function useCaseSearchForContact(callId: number | null, contactId: number | null) {
  return useQuery({
    queryKey: [...queueKeys.all, 'case-search', callId, contactId] as const,
    queryFn: () => queueRepository.searchCasesForContact(callId!, contactId!),
    enabled: callId !== null && contactId !== null,
  });
}

/**
 * Hook to make a call to the next available number in the queue
 */
export function useMakeNextCall() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => queueRepository.makeNextCall(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queueKeys.lists() });
    },
  });
}
